import React, { useState, useMemo, useEffect } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useDisponibilidadEspacio } from '../../hooks/useEspacios'
import { getStatusColors } from '../../utils/statusColorMap'
import { stringToDate, dateToString, parseLocalDateString, formatLocalDateToString, formatDateDisplay } from '../../utils/dateHelpers'
import { getMotivColor } from '../../types/availability'
import { formatCurrency } from '../../utils/currencyHelpers'
import type { EspacioDisponible } from '../../types'
import type { DiaDesglose } from '../../types/availability'

interface DateSelectionData {
  fechaEntrada: string
  fechaSalida: string
  desglose?: Array<{fecha: string; dia_semana?: string; tarifa: number}>
  montoTotal?: number
}

interface DateAvailabilityModalProps {
  espacio?: EspacioDisponible  // Backward compatibility: un espacio singular
  espacios?: EspacioDisponible[]  // ✅ Array de múltiples espacios
  isOpen: boolean
  onClose: () => void
  onSelectDates: (data: DateSelectionData) => void
  rangoOriginal: {
    fecha_entrada: string
    fecha_salida: string
  }
  fechaEntradaPreseleccionada?: string
  fechaSalidaPreseleccionada?: string
  modo?: 'checkout' | 'carrito'  // 'checkout' = Confirmar y Reservar (V1), 'carrito' = Agregar al carrito (V2)
}

const DateAvailabilityModal: React.FC<DateAvailabilityModalProps> = ({
  espacio: espacioSingular,
  espacios,
  isOpen,
  onClose,
  onSelectDates,
  rangoOriginal,
  fechaEntradaPreseleccionada,
  fechaSalidaPreseleccionada,
  modo = 'checkout'  // Por defecto es checkout (V1)
}) => {
  const { colors } = useTheme()
  // ✅ Si hay múltiples espacios, usar el array; si no, convertir el singular a array
  const espaciosLista = espacios || (espacioSingular ? [espacioSingular] : [])
  const [espacioSeleccionadoId, setEspacioSeleccionadoId] = useState<string>(espaciosLista[0]?.id || '')
  const espacioActual = espaciosLista.find(e => e.id === espacioSeleccionadoId) || espaciosLista[0]

  const [fechaEntradaSeleccionada, setFechaEntradaSeleccionada] = useState<string | null>(fechaEntradaPreseleccionada || null)
  const [fechaSalidaSeleccionada, setFechaSalidaSeleccionada] = useState<string | null>(fechaSalidaPreseleccionada || null)
  const [alertMessage, setAlertMessage] = useState<string | null>(null)
  const [showAlertModal, setShowAlertModal] = useState(false)
  const [hoveredDay, setHoveredDay] = useState<string | null>(null)

  // ✅ Fetch desglose detallado con motivo_indisponibilidad
  const { data: detalleEspacio, isLoading: loadingDetalle } = useDisponibilidadEspacio(
    espacioSeleccionadoId,
    espacioSeleccionadoId ? {
      fecha_entrada: rangoOriginal.fecha_entrada,
      fecha_salida: rangoOriginal.fecha_salida
    } : null
  )

  // ✅ Detectar dispositivo (móvil/tablet/desktop)
  const { isMobile } = useDeviceDetection()

  // ✅ Actualizar fechas cuando cambian las preseleccionadas
  useEffect(() => {
    if (fechaEntradaPreseleccionada) {
      setFechaEntradaSeleccionada(fechaEntradaPreseleccionada)
    }
    if (fechaSalidaPreseleccionada) {
      setFechaSalidaSeleccionada(fechaSalidaPreseleccionada)
    }
  }, [fechaEntradaPreseleccionada, fechaSalidaPreseleccionada])

  // ✅ Convertir arrays a Sets para búsqueda rápida
  // Priorizar detalleEspacio (que tiene tarifas) sobre espacioActual (que viene de disponibilidad_simple sin tarifas)
  const diasDisponiblesSet = useMemo(() =>
    new Set(detalleEspacio?.dias_disponibles || espacioActual?.dias_disponibles || []),
    [detalleEspacio?.dias_disponibles, espacioActual?.dias_disponibles]
  )
  const diasOcupadosSet = useMemo(() =>
    new Set(detalleEspacio?.dias_ocupados || espacioActual?.dias_ocupados || []),
    [detalleEspacio?.dias_ocupados, espacioActual?.dias_ocupados]
  )

  // ✅ Crear mapa de desglose para acceso rápido por fecha
  const desgloseMap = useMemo(() => {
    const map = new Map<string, DiaDesglose>()
    detalleEspacio?.desglose?.forEach(dia => {
      map.set(dia.fecha, dia)
    })
    return map
  }, [detalleEspacio?.desglose])

  // ✅ Obtener unidad_reserva correcta (priorizar detalleEspacio)
  const unidadReserva = useMemo(() =>
    detalleEspacio?.unidad_reserva || espacioActual?.unidad_reserva || 'noche',
    [detalleEspacio?.unidad_reserva, espacioActual?.unidad_reserva]
  )

  // ✅ La tarifa correcta se calcula en el backend
  // Este componente solo muestra las tarifas del desglose (NO RECALCULA)
  // El desglose contiene la tarifa exacta para cada día según la jerarquía de tarifas

  // Generar calendario - parsear como fechas locales, no UTC
  const fechaInicio = parseLocalDateString(rangoOriginal.fecha_entrada)
  const fechaFin = parseLocalDateString(rangoOriginal.fecha_salida)

  // Calcular meses a mostrar
  const meses = useMemo(() => {
    const resultado: { year: number; month: number; dias: Date[] }[] = []
    const fecha = new Date(fechaInicio)
    fecha.setDate(1) // Primer día del mes

    while (fecha <= fechaFin) {
      const year = fecha.getFullYear()
      const month = fecha.getMonth()
      const diasDelMes: Date[] = []

      // Agregar días del mes que están en el rango
      const primerDia = new Date(year, month, 1)
      const ultimoDia = new Date(year, month + 1, 0)

      for (let d = new Date(primerDia); d <= ultimoDia; d.setDate(d.getDate() + 1)) {
        const fechaCopia = new Date(d)
        // Incluir la fechaFin (solo se permite same-day para unidad_reserva='dia')
        if (fechaCopia >= fechaInicio && fechaCopia <= fechaFin) {
          diasDelMes.push(fechaCopia)
        }
      }

      if (diasDelMes.length > 0) {
        resultado.push({ year, month, dias: diasDelMes })
      }

      fecha.setMonth(fecha.getMonth() + 1)
    }

    return resultado
  }, [fechaInicio, fechaFin])

  const isDiaDisponible = (fecha: Date): boolean => {
    const fechaStr = formatLocalDateToString(fecha)
    return diasDisponiblesSet.has(fechaStr)
  }

  const isDiaOcupado = (fecha: Date): boolean => {
    const fechaStr = formatLocalDateToString(fecha)
    return diasOcupadosSet.has(fechaStr)
  }

  const isDiaSeleccionado = (fecha: Date): boolean => {
    const fechaStr = formatLocalDateToString(fecha)
    if (fechaEntradaSeleccionada && !fechaSalidaSeleccionada) {
      return fechaStr === fechaEntradaSeleccionada
    }
    if (fechaEntradaSeleccionada && fechaSalidaSeleccionada) {
      return fechaStr >= fechaEntradaSeleccionada && fechaStr <= fechaSalidaSeleccionada
    }
    return false
  }

  const handleClickDia = (fecha: Date) => {
    const fechaStr = formatLocalDateToString(fecha)

    // Si no hay fecha de entrada, seleccionarla (DEBE estar disponible)
    if (!fechaEntradaSeleccionada) {
      if (!isDiaDisponible(fecha)) {
        return
      }
      setFechaEntradaSeleccionada(fechaStr)
      setFechaSalidaSeleccionada(null)
      return
    }

    // Si hay fecha de entrada pero no salida
    if (fechaEntradaSeleccionada && !fechaSalidaSeleccionada) {
      // Verificar que la fecha de salida sea posterior o igual
      if (fechaStr < fechaEntradaSeleccionada) {
        // Reset y comenzar de nuevo
        if (!isDiaDisponible(fecha)) {
          return
        }
        setFechaEntradaSeleccionada(fechaStr)
        setFechaSalidaSeleccionada(null)
        return
      }

      // ✅ VALIDACIÓN: Para unidad_reserva='noche', no permitir mismo día
      if (unidadReserva === 'noche' && fechaStr === fechaEntradaSeleccionada) {
        setAlertMessage('Para reservas por noche, debes seleccionar una fecha de salida posterior a la fecha de entrada (mínimo 1 noche).')
        setShowAlertModal(true)
        return
      }

      // ✅ Para "noche": fecha_salida NO necesita estar disponible (es checkout)
      // ✅ Para "día": fecha_salida DEBE estar disponible
      const esNoche = unidadReserva === 'noche'
      if (!esNoche && !isDiaDisponible(fecha)) {
        // Para "día", la fecha de salida debe estar disponible
        return
      }

      // Verificar que no haya días ocupados en el rango
      const rangoValido = validarRango(fechaEntradaSeleccionada, fechaStr)
      if (!rangoValido) {
        setAlertMessage('El rango seleccionado contiene días no disponibles. Por favor selecciona un rango continuo.')
        setShowAlertModal(true)
        setFechaEntradaSeleccionada(null)
        setFechaSalidaSeleccionada(null)
        return
      }

      setFechaSalidaSeleccionada(fechaStr)
      return
    }

    // Si ya hay ambas fechas, resetear (DEBE estar disponible)
    if (!isDiaDisponible(fecha)) {
      return
    }
    setFechaEntradaSeleccionada(fechaStr)
    setFechaSalidaSeleccionada(null)
  }

  const validarRango = (inicio: string, fin: string): boolean => {
    // Parsear como fechas locales, no UTC
    const fechaInicio = parseLocalDateString(inicio)
    const fechaFin = parseLocalDateString(fin)
    let fecha = new Date(fechaInicio.getFullYear(), fechaInicio.getMonth(), fechaInicio.getDate())

    // ✅ Para "noche": no validar el último día (es checkout, no noche de estancia)
    // ✅ Para "día": validar hasta el último día (inclusive)
    const esNoche = unidadReserva === 'noche'
    const fechaValidacionFin = esNoche
      ? new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() - 1)
      : fechaFin

    while (fecha <= fechaValidacionFin) {
      // Usar formatLocalDateToString para no convertir a UTC
      const fechaStr = formatLocalDateToString(fecha)
      if (!diasDisponiblesSet.has(fechaStr)) {
        return false
      }
      fecha.setDate(fecha.getDate() + 1)
    }

    return true
  }

  // ✅ Obtener información del motivo para un día (desde desglose detallado)
  const getMotivoInfo = (fecha: Date): {
    motivo: string | null | undefined
    color: string
    tarifas: { simple?: number; multiple?: number | null; nombre_simple?: string; nombre_multiple?: string | null } | null
  } => {
    const fechaStr = formatLocalDateToString(fecha)
    const diaDesglose = desgloseMap.get(fechaStr)

    if (!diaDesglose) {
      // Si no hay desglose, determinar por disponibilidad simple
      const disponible = diasDisponiblesSet.has(fechaStr)
      return {
        motivo: null,
        color: disponible ? '#22c55e' : '#ef4444',
        tarifas: null
      }
    }

    return {
      motivo: diaDesglose.motivo_indisponibilidad,
      color: getMotivColor(diaDesglose.motivo_indisponibilidad),
      tarifas: {
        simple: diaDesglose.tarifa_simple,
        multiple: diaDesglose.tarifa_multiple,
        nombre_simple: diaDesglose.nombre_tarifa_simple,
        nombre_multiple: diaDesglose.nombre_tarifa_multiple
      }
    }
  }

  // ✅ Obtener tarifa de un día específico (SIMPLIFICADO - igual que DateSelectorV2)
  const getTarifaDia = (fechaStr: string): number | undefined => {
    const unidades = calcularUnidades()

    // ✅ Usar SOLO el desglose del hook useDisponibilidadEspacio (igual que DateSelectorV2)
    const dia = desgloseMap.get(fechaStr)
    if (!dia) {
      return undefined
    }

    // Si 0-1 unidad: tarifa_simple
    // Si 2+ unidades: tarifa_multiple (si existe), sino tarifa_simple
    if (unidades === 0 || unidades === 1) {
      return dia.tarifa_simple
    }

    return dia.tarifa_multiple ?? dia.tarifa_simple
  }

  const handleConfirmar = () => {
    if (fechaEntradaSeleccionada && fechaSalidaSeleccionada) {
      // ✅ Validar fechas según unidad_reserva ANTES de confirmar
      const entrada = parseLocalDateString(fechaEntradaSeleccionada)
      const salida = parseLocalDateString(fechaSalidaSeleccionada)

      if (unidadReserva === 'noche') {
        // Para noche: fecha_salida DEBE ser > fecha_entrada (no permite mismo día)
        if (entrada.getTime() >= salida.getTime()) {
          setAlertMessage(`Para reservas por noche, la fecha de salida debe ser posterior a la fecha de entrada`)
          setShowAlertModal(true)
          return
        }
      } else if (unidadReserva === 'dia') {
        // Para día: fecha_salida DEBE ser >= fecha_entrada (permite mismo día)
        if (entrada.getTime() > salida.getTime()) {
          setAlertMessage(`Para reservas por día, la fecha de salida debe ser igual o posterior a la fecha de entrada`)
          setShowAlertModal(true)
          return
        }
      }

      // ✅ Preparar datos completos con desglose y monto total
      const cantidadUnidades = calcularUnidades()
      const usar_multiple = cantidadUnidades >= 2

      // Calcular fecha final según unidad_reserva (para filtrar desglose)
      const esNoche = unidadReserva === 'noche'
      const fechaFinal = new Date(salida)
      if (esNoche) {
        fechaFinal.setDate(fechaFinal.getDate() - 1)
      }

      const desgloseSeleccionado = detalleEspacio?.desglose?.filter(dia => {
        const fecha = parseLocalDateString(dia.fecha)
        return fecha >= entrada && fecha <= fechaFinal
      }).map(dia => ({
        // ✅ Pasar solo la tarifa que aplica (sin tarifa_simple y tarifa_multiple)
        fecha: dia.fecha,
        dia_semana: dia.dia_semana,
        tarifa: usar_multiple ? (dia.tarifa_multiple || dia.tarifa_simple) : dia.tarifa_simple
      })) || []

      const montoTotal = calcularTotal()

      const selectionData: DateSelectionData = {
        fechaEntrada: fechaEntradaSeleccionada,
        fechaSalida: fechaSalidaSeleccionada,
        desglose: desgloseSeleccionado,
        montoTotal: montoTotal
      }

      onSelectDates(selectionData)
      onClose()
    }
  }

  const calcularUnidades = (): number => {
    if (!fechaEntradaSeleccionada || !fechaSalidaSeleccionada) return 0
    // Parsear como fechas locales, no UTC
    const inicio = parseLocalDateString(fechaEntradaSeleccionada)
    const fin = parseLocalDateString(fechaSalidaSeleccionada)
    const diasDiferencia = Math.floor((fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24))

    // Si es por día, agregar 1 (ej: 15 al 20 = 6 días)
    // Si es por noche, devolver tal cual (ej: 15 al 20 = 5 noches)
    return unidadReserva === 'dia' ? diasDiferencia + 1 : diasDiferencia
  }

  const calcularTotal = (): number => {
    if (!fechaEntradaSeleccionada || !fechaSalidaSeleccionada) {
      return 0
    }

    // ✅ Calcular total siempre desde desglose (suma día por día)
    // El backend retorna desglose con tarifa_simple/tarifa_multiple ya calculados
    const fechaInicio = parseLocalDateString(fechaEntradaSeleccionada)
    const fechaFin = parseLocalDateString(fechaSalidaSeleccionada)
    const cantidadUnidades = calcularUnidades()

    let total = 0
    let fechaActual = new Date(fechaInicio)

    // ✅ Para "noche": iterar hasta fecha_fin - 1 (solo inicios de noche)
    // ✅ Para "dia": iterar hasta fecha_fin (inclusive)
    const esNoche = unidadReserva === 'noche'
    const fechaActualFin = new Date(fechaFin)
    if (esNoche) {
      fechaActualFin.setDate(fechaActualFin.getDate() - 1)
    }

    while (fechaActual <= fechaActualFin) {
      const fechaStr = formatLocalDateToString(fechaActual)
      const tarifa = getTarifaDia(fechaStr)
      if (tarifa !== undefined) {
        total += tarifa
      }
      fechaActual.setDate(fechaActual.getDate() + 1)
    }

    return total
  }


  const obtenerEtiquetaUnidad = (): string => {
    const unidades = calcularUnidades()
    if (unidadReserva === 'dia') {
      return `${unidades} día${unidades !== 1 ? 's' : ''}`
    }
    return `${unidades} noche${unidades !== 1 ? 's' : ''}`
  }

  const obtenerEtiquetaTarifa = (): string => {
    return unidadReserva === 'dia' ? 'Tarifa por día' : 'Tarifa por noche'
  }

  // Modal de alerta personalizado
  if (showAlertModal && alertMessage) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header con icono */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Información importante</h3>
          </div>

          {/* Contenido */}
          <div className="px-6 py-6">
            <p className="text-gray-700 text-center leading-relaxed">
              {alertMessage}
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl">
            <button
              onClick={() => setShowAlertModal(false)}
              style={{ backgroundColor: colors.primary }}
              className="w-full px-4 py-2 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!isOpen) return null

  const mesesNombres = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ]

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

  // ✅ Alias para usar en el JSX sin cambiar cientos de referencias
  const espacio = espacioActual

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-auto max-h-[95vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                {espaciosLista.length > 1
                  ? `Selecciona fechas para ${espaciosLista.length} espacios`
                  : `Selecciona fechas - ${espaciosLista[0]?.nombre}`}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Selecciona las fechas disponibles para tu estadía
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Leyenda */}
          <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-green-500 rounded"></div>
              <span className="text-sm text-gray-700">Disponible</span>
            </div>
            {unidadReserva === 'noche' && (
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded border-2" style={{ backgroundColor: '#dbeafe', borderColor: '#3b82f6' }}></div>
                <span className="text-sm text-gray-700">Válido para checkout</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: '#ef4444' }}></div>
              <span className="text-sm text-gray-700">📅 Reserva</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: '#f97316' }}></div>
              <span className="text-sm text-gray-700">⏱️ Bloqueo</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-gray-400 rounded"></div>
              <span className="text-sm text-gray-700">🔒 No disponible</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.primary }}></div>
              <span className="text-sm text-gray-700">Seleccionado</span>
            </div>
          </div>

          {/* ✅ Selector de espacio si hay múltiples */}
          {espaciosLista.length > 1 && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="text-sm font-medium text-blue-900 block mb-2">Selecciona espacio para ver disponibilidad:</label>
              <select
                value={espacioSeleccionadoId}
                onChange={(e) => setEspacioSeleccionadoId(e.target.value)}
                className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm"
              >
                {espaciosLista.map((esp) => (
                  <option key={esp.id} value={esp.id}>
                    {esp.nombre} ({esp.codigo})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Estadísticas */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700 font-medium">{unidadReserva === 'dia' ? 'Días' : 'Noches'} disponibles</p>
              <p className="text-2xl font-bold text-green-900">{detalleEspacio?.dias_disponibles?.length || espacio.total_dias_disponibles || '-'}</p>
              <p className="text-xs text-green-600">de {detalleEspacio?.desglose?.length || espacio.total_dias_rango || '-'} {unidadReserva === 'dia' ? 'días' : 'noches'} totales</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              {loadingDetalle ? (
                <p className="text-xs text-blue-500 italic">Cargando tarifas...</p>
              ) : detalleEspacio?.desglose && detalleEspacio.desglose.length > 0 ? (
                (() => {
                  // Extraer tarifas únicas del desglose
                  const tarifasMap = new Map<string, {
                    nombre_simple: string;
                    tarifa_simple: number;
                    nombre_multiple: string | null;
                    tarifa_multiple: number | null;
                  }>();

                  detalleEspacio.desglose.forEach(dia => {
                    const key = `${dia.nombre_tarifa_simple}|${dia.tarifa_simple}`;
                    if (!tarifasMap.has(key)) {
                      tarifasMap.set(key, {
                        nombre_simple: dia.nombre_tarifa_simple || 'Tarifa',
                        tarifa_simple: dia.tarifa_simple || 0,
                        nombre_multiple: dia.nombre_tarifa_multiple,
                        tarifa_multiple: dia.tarifa_multiple
                      });
                    }
                  });

                  const tarifasArray = Array.from(tarifasMap.values());

                  return (
                    <>
                      <p className="text-sm text-blue-700 font-medium">💰 Tarifas disponibles:</p>
                      <div className="space-y-1 mt-2">
                        {tarifasArray.slice(0, 2).map((tarifa, idx) => (
                          <div key={idx} className="text-xs text-blue-700 flex justify-between">
                            <span>{tarifa.nombre_simple}</span>
                            <span className="font-semibold">
                              {formatCurrency(tarifa.tarifa_simple)}{tarifa.tarifa_multiple ? `/${formatCurrency(tarifa.tarifa_multiple)}` : ''}
                            </span>
                          </div>
                        ))}
                        {tarifasArray.length > 2 && (
                          <p className="text-xs text-blue-500 italic">+{tarifasArray.length - 2} más</p>
                        )}
                      </div>
                      <p className="text-xs text-blue-600 mt-2">Cada día muestra su tarifa</p>
                    </>
                  );
                })()
              ) : espacio.tarifas && espacio.tarifas.length > 0 ? (
                <>
                  <p className="text-sm text-blue-700 font-medium">💰 Tarifas disponibles:</p>
                  <div className="space-y-1 mt-2">
                    {espacio.tarifas.slice(0, 2).map((tarifa) => (
                      <div key={tarifa.id} className="text-xs text-blue-700 flex justify-between">
                        <span>{tarifa.nombre}</span>
                        <span className="font-semibold">
                          {formatCurrency(tarifa.tarifa_simple)}{tarifa.tarifa_multiple ? `/${formatCurrency(tarifa.tarifa_multiple)}` : ''}
                        </span>
                      </div>
                    ))}
                    {espacio.tarifas.length > 2 && (
                      <p className="text-xs text-blue-500 italic">+{espacio.tarifas.length - 2} más</p>
                    )}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Cada día muestra su tarifa</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-blue-700 font-medium">{obtenerEtiquetaTarifa()}</p>
                  {espacio.tarifa_simple !== undefined ? (
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(espacio.tarifa_simple)}</p>
                  ) : (
                    <p className="text-xs text-blue-500 italic">-</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Calendario */}
          <div className="space-y-8">
            {meses.map(({ year, month, dias }) => (
              <div key={`${year}-${month}`}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {mesesNombres[month]} {year}
                </h3>

                <div className="grid grid-cols-7 gap-2">
                  {/* Headers días de la semana */}
                  {diasSemana.map(dia => (
                    <div key={dia} className="text-center text-xs font-semibold text-gray-600 py-2">
                      {dia}
                    </div>
                  ))}

                  {/* Espacios vacíos antes del primer día */}
                  {Array.from({ length: dias[0].getDay() }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}

                  {/* Días del mes */}
                  {dias.map(fecha => {
                    const disponible = isDiaDisponible(fecha)
                    const seleccionado = isDiaSeleccionado(fecha)
                    const fechaStr = formatLocalDateToString(fecha)
                    const motivoInfo = getMotivoInfo(fecha)

                    let borderColor = '#D1D5DB'  // Gris por defecto
                    let borderWidth = '1px'
                    let backgroundColor = '#FFFFFF'
                    let textColor = '#6B7280'
                    let cursorStyle = 'not-allowed'

                    // ✅ Color según motivo (del desglose detallado)
                    if (!disponible && motivoInfo.motivo) {
                      // Color basado en el motivo específico
                      borderColor = motivoInfo.color
                      borderWidth = '2px'
                      backgroundColor = motivoInfo.color + '20'  // Color con opacidad
                      textColor = motivoInfo.color
                      cursorStyle = 'not-allowed'
                    } else if (disponible) {
                      // Disponible: verde en el borde
                      borderColor = '#22C55E'
                      borderWidth = '2px'
                      backgroundColor = '#F0FDF4'
                      textColor = '#15803D'
                      cursorStyle = 'pointer'
                    }

                    if (seleccionado) {
                      // Seleccionado: borde grueso del color del tema
                      borderColor = colors.primary
                      borderWidth = '3px'
                      backgroundColor = colors.primary + '15'  // Color del tema con opacidad
                      textColor = colors.primary
                    }

                    // ✅ Para noche: fecha_salida puede estar ocupada (es checkout)
                    // Para día: todos los días deben estar disponibles
                    const esNoche = unidadReserva === 'noche'
                    const puedeSerFechaSalida = esNoche && fechaEntradaSeleccionada && !fechaSalidaSeleccionada && fecha > parseLocalDateString(fechaEntradaSeleccionada)
                    const botonDeshabilitado = !disponible && !puedeSerFechaSalida

                    // ✅ Styling especial para días válidos como checkout (solo para 'noche')
                    if (puedeSerFechaSalida && !disponible) {
                      backgroundColor = '#dbeafe'  // Azul claro
                      textColor = '#1e40af'       // Azul oscuro
                      borderColor = '#3b82f6'     // Azul medio
                      borderWidth = '2px'
                      cursorStyle = 'pointer'
                    }

                    return (
                      <div key={fecha.toISOString()} className="relative">
                        <button
                          onClick={() => handleClickDia(fecha)}
                          disabled={botonDeshabilitado}
                          onMouseEnter={(e) => {
                            // ✅ Mostrar tooltip (tanto para disponibles como no disponibles)
                            setHoveredDay(fechaStr)
                            // ✅ Efecto hover para días disponibles o válidos para checkout
                            if ((disponible || puedeSerFechaSalida) && !seleccionado) {
                              e.currentTarget.style.transform = 'scale(1.05)'
                              e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primaryDark}40`
                            }
                          }}
                          onMouseLeave={(e) => {
                            // ✅ Remover tooltip
                            setHoveredDay(null)
                            // ✅ Remover efecto hover
                            if ((disponible || puedeSerFechaSalida) && !seleccionado) {
                              e.currentTarget.style.transform = 'scale(1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }
                          }}
                          className="rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center p-2 min-h-20 w-full"
                          style={{
                            backgroundColor,
                            color: textColor,
                            cursor: botonDeshabilitado ? 'not-allowed' : cursorStyle,
                            border: `${borderWidth} solid ${borderColor}`,
                            opacity: botonDeshabilitado ? 0.5 : 1
                          }}
                        >
                          <span className="font-bold">{fecha.getDate()}</span>
                          {disponible && !isMobile && (
                            // ✅ Mostrar tarifa del desglose para este día (SOLO en desktop, no en mobile)
                            (() => {
                              const tarifa = getTarifaDia(fechaStr)
                              return tarifa !== undefined ? (
                                <span className="text-xs">{formatCurrency(tarifa)}</span>
                              ) : null
                            })()
                          )}
                        </button>

                        {/* ✅ Tooltip para días NO disponibles (solo desktop con hover) */}
                        {!disponible && motivoInfo.motivo && !isMobile && hoveredDay === fechaStr && (
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 bg-white shadow-lg rounded-lg p-3 min-w-max text-xs border-l-4 pointer-events-none" style={{ borderLeftColor: motivoInfo.color }}>
                            <div className="font-semibold" style={{ color: motivoInfo.color }}>{motivoInfo.motivo}</div>
                            {motivoInfo.tarifas?.simple && (
                              <div className="text-gray-600 mt-1">
                                Tarifa: {formatCurrency(motivoInfo.tarifas.simple)}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ✅ Tooltip para días DISPONIBLES con nombre de tarifa (solo desktop con hover) */}
                        {disponible && motivoInfo.tarifas && !isMobile && hoveredDay === fechaStr && (
                          <div className="absolute z-10 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg bottom-full mb-1 left-1/2 transform -translate-x-1/2 whitespace-nowrap pointer-events-none">
                            <div className="font-semibold">{motivoInfo.tarifas.nombre_simple || 'Tarifa'}</div>
                            <div>{formatCurrency(motivoInfo.tarifas.simple || 0)}/{unidadReserva}</div>
                            {motivoInfo.tarifas.multiple && (
                              <div className="text-xs text-gray-300 mt-1">
                                {motivoInfo.tarifas.nombre_multiple || 'Múltiple'}: {formatCurrency(motivoInfo.tarifas.multiple)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Reservas existentes */}
          {espacio.reservas_existentes && espacio.reservas_existentes.length > 0 && (
            <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h4 className="text-sm font-semibold text-orange-900 mb-3">
                ⚠️ Períodos no disponibles en el rango
              </h4>
              <ul className="space-y-2">
                {espacio.reservas_existentes.map((reserva, idx) => {
                  const tipoLabel = {
                    'reserva': '📅 Reserva confirmada',
                    'bloqueo': '⏱️ Bloqueo temporal',
                    'no-disponible': '🔒 Bloqueado (mantenimiento/limpieza/privado)'
                  }[reserva.tipo] || reserva.tipo

                  return (
                    <li key={idx} className="text-xs text-orange-800 flex items-start gap-2">
                      <span className="flex-shrink-0 mt-0.5">•</span>
                      <div>
                        <div className="font-semibold">{tipoLabel}</div>
                        <div className="text-orange-700">
                          {formatDateDisplay(reserva.fecha_entrada)} - {formatDateDisplay(reserva.fecha_salida)}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
          {fechaEntradaSeleccionada && fechaSalidaSeleccionada ? (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-blue-700 font-medium">Fechas seleccionadas</p>
                  <p className="text-sm sm:text-lg font-bold text-blue-900 mt-1">
                    {formatDateDisplay(fechaEntradaSeleccionada)} - {formatDateDisplay(fechaSalidaSeleccionada)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">{obtenerEtiquetaUnidad()}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs sm:text-sm text-blue-700 font-medium">Total</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-900 mt-1">{formatCurrency(calcularTotal())}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                {fechaEntradaSeleccionada
                  ? '📅 Ahora selecciona la fecha de salida'
                  : '👆 Haz click en un día disponible para comenzar'}
              </p>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmar}
              disabled={!fechaEntradaSeleccionada || !fechaSalidaSeleccionada || calcularTotal() <= 0}
              style={{
                backgroundColor: colors.primary,
                color: '#FFFFFF'
              }}
              className="flex-1 px-6 py-3 font-semibold rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={calcularTotal() <= 0 ? 'No se pudieron cargar las tarifas' : ''}
            >
              {modo === 'carrito' ? 'Agregar al Carrito' : 'Confirmar y Reservar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DateAvailabilityModal

export type { DateSelectionData }
